import { BreadcrumbGroup, SpaceBetween, Tabs } from "@amzn/awsui-components-react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useContext, useEffect, useState } from "react";
import { AppContext } from "../../../utils/appContext";
import { JunoCollectionGroupOverviewParams } from "../../../models/types";
import { useGetCollectionGroupDetailsQuery } from "../../hooks/collectiongroup/useGetCollectionDetailsQuery";
import { CollectionGroupDetails } from "./CollectionGroupDetails";
import { BREADCRUMBS } from "../../../utils/constants";
import { JUNO_BASE_PATH } from "../../commons/constants";
import { CollectionGroupDashboards } from "./CollectionGroupDashboards";
import { useGetLazyExternalCwQuery } from "../../Components/Cloudwatch/hooks/useGetExternalCwQuery";
import { get } from "lodash";
import { CG_OCU_METRICS, CG_OCU_METRICS_DIMENSIONS } from "./constants";
import { CollectionGroupCollectionsList } from "./CollectionGroupCollectionsList";

const CollectionGroupOverview = (props) => {
    const location = useLocation();
    const appContext = useContext(AppContext);
    const history = useHistory();
    const { awsAccountId, collectionGroupId } =
        useParams<JunoCollectionGroupOverviewParams>();
    const [activeTabId, setActiveTabId] = useState('overview');

    const [collectionGroupInformation, setCollectionGroupInformation] = useState();
    const [collectionGroupName, setCollectionGroupName] = useState();

    // set the min/max for each indexing and search
    const [indexingOCUMin, setIndexingOCUMin] = useState(null);
    const [indexingOCUMax, setIndexingOCUMax] = useState(null);
    const [searchOCUMin, setSearchOCUMin] = useState(null);
    const [searchOCUMax, setSearchOCUMax] = useState(null);


    const [
        getCollectionGroupDetails,
        { loading: collectionGroupLoading, data: collectionGroupDetails },
    ] = useGetCollectionGroupDetailsQuery();

    const [
        getCgSearchOCUCount,
        { loading: SearchOCUsLoading, data: searchOCUs },
    ] = useGetLazyExternalCwQuery();

    const [
        getCgIndexingOCUCount,
        { loading: IndexingOCUsLoading, data: indexingOCUs },
    ] = useGetLazyExternalCwQuery();

    useEffect(() => {
        const curr_time = new Date();
        const start_time = new Date();
        start_time.setHours(curr_time.getHours() - 2);

        getCgIndexingOCUCount({
            variables: {
                namespace: CG_OCU_METRICS.namespace,
                metric_name: 'CxMetricsIndexingOCU',
                dimensions: `[{"Name":"${CG_OCU_METRICS_DIMENSIONS.AWS_ACCOUNT_ID}","Value":"${awsAccountId}"},` +
                    `{"Name":"${CG_OCU_METRICS_DIMENSIONS.COLLECTION_GROUP_ID}","Value":"${collectionGroupId}"},` +
                    `{"Name":"${CG_OCU_METRICS_DIMENSIONS.COLLECTION_GROUP_NAME}","Value":"${collectionGroupName}"}]`,
                startTime: start_time,
                endTime: curr_time,
                period: 3600,
                statistics: 'Maximum'
            }
        });
        getCgSearchOCUCount({
            variables: {
                namespace: CG_OCU_METRICS.namespace,
                metric_name: 'CxMetricsSearchOCU',
                dimensions: `[{"Name":"${CG_OCU_METRICS_DIMENSIONS.AWS_ACCOUNT_ID}","Value":"${awsAccountId}"},` +
                    `{"Name":"${CG_OCU_METRICS_DIMENSIONS.COLLECTION_GROUP_ID}","Value":"${collectionGroupId}"},` +
                    `{"Name":"${CG_OCU_METRICS_DIMENSIONS.COLLECTION_GROUP_NAME}","Value":"${collectionGroupName}"}]`,
                startTime: start_time,
                endTime: curr_time,
                period: 3600,
                statistics: 'Maximum'
            }
        });
    }, [awsAccountId, collectionGroupId, collectionGroupName]);

    useEffect(() => {
        if (!collectionGroupDetails) return;
        let collectionGroupInfo =
            collectionGroupDetails.getCollectionGroupDetails.collection_group_details;
        if (collectionGroupInfo && collectionGroupInfo.length > 0) {
            setCollectionGroupInformation(collectionGroupInfo[0]);
            setCollectionGroupName(collectionGroupInfo[0].name);

            // set the min/max for each indexing and search
            setIndexingOCUMin(collectionGroupInfo[0].capacity_limits?.min_indexing_capacity_in_ocu);
            setIndexingOCUMax(collectionGroupInfo[0].capacity_limits?.max_indexing_capacity_in_ocu);
            setSearchOCUMin(collectionGroupInfo[0].capacity_limits?.min_search_capacity_in_ocu);
            setSearchOCUMax(collectionGroupInfo[0].capacity_limits?.max_search_capacity_in_ocu);

        }
    }, [collectionGroupLoading, collectionGroupDetails]);

    useEffect(() => {
        getCollectionGroupDetails({
            variables: {
                awsAccountId,
                collectionGroupId,
                collectionGroupName: '',
            }
        })
    }, [awsAccountId, collectionGroupId]);

    useEffect(() => {
        appContext.setBreadcrumbs([
            {
                ...BREADCRUMBS.JUNO,
                href: `${JUNO_BASE_PATH}`,
                onClick: () => {
                    history.push(`${JUNO_BASE_PATH}`);
                },
            },
            {
                text: awsAccountId,
                href: `${JUNO_BASE_PATH}/${awsAccountId}`,
                onClick: () => {
                    history.push(`${JUNO_BASE_PATH}/${awsAccountId}`);
                },
            },
            { text: collectionGroupId, href: `/${awsAccountId}/collection-group/${collectionGroupId}` },
        ]);
    }, [location]);

    const tabs = [
        {
            id: 'overview',
            label: 'Overview',
            content: (
                <SpaceBetween direction="vertical" size="l">
                    <CollectionGroupDetails
                        collectionGroupId={collectionGroupId}
                        collectionGroupDetails={collectionGroupInformation}
                        awsAccountId={awsAccountId}
                        searchOCUsCount = {get(searchOCUs, 'getJunoCwMetrics.externalMetrics.values[0]', undefined)}
                        indexingOCUsCount = {get(indexingOCUs, 'getJunoCwMetrics.externalMetrics.values[0]', undefined)}

                    />
                    <CollectionGroupCollectionsList
                        awsAccountId={awsAccountId}
                        collectionGroupId={collectionGroupId}
                        collectionGroupDetails={collectionGroupInformation}
                    />
                    <CollectionGroupDashboards
                        collectionGroupDetails={collectionGroupInformation}
                        awsAccountId={awsAccountId}
                        collectionGroupId={collectionGroupId}
                        collectionGroupName={collectionGroupName}
                        ocuLimits={{
                            indexing: {
                                minValue: indexingOCUMin,
                                maxValue: indexingOCUMax,
                                minLabel: 'Min OCU',
                                maxLabel: 'Max OCU'
                            },
                            search: {
                                minValue: searchOCUMin,
                                maxValue: searchOCUMax,
                                minLabel: 'Min OCU',
                                maxLabel: 'Max OCU'
                            }
                        }}
                    />
                </SpaceBetween>
            ),
            dismissable: false,
            active: true
        }

    ];

    return (
        <SpaceBetween direction="vertical" size="l">
            <BreadcrumbGroup items={appContext.breadcrumbs} />
            <Tabs
                tabs={tabs}
                activeTabId={activeTabId}
                onChange={({ detail }) => setActiveTabId(detail.activeTabId)}
            />
        </SpaceBetween>
    );
}

export { CollectionGroupOverview }
